import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';

const MediaGrid = ({ images = [] }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Single image
  if (images.length === 1) {
    return (
      <>
        <div className="rounded-lg overflow-hidden cursor-pointer" onClick={() => openLightbox(0)}>
          <img
            src={images[0]}
            alt="Post media"
            className="w-full max-h-[500px] object-cover hover:opacity-95 transition-opacity"
          />
        </div>
        {renderLightbox()}
      </>
    );
  }

  // Two images - side by side
  if (images.length === 2) {
    return (
      <>
        <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden">
          {images.map((img, index) => (
            <div key={index} className="cursor-pointer" onClick={() => openLightbox(index)}>
              <img
                src={img}
                alt={`Post media ${index + 1}`}
                className="w-full h-full max-h-[300px] object-cover hover:opacity-95 transition-opacity"
              />
            </div>
          ))}
        </div>
        {renderLightbox()}
      </>
    );
  }

  // Three images - one large, two small
  if (images.length === 3) {
    return (
      <>
        <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden">
          <div className="col-span-2 cursor-pointer" onClick={() => openLightbox(0)}>
            <img
              src={images[0]}
              alt="Post media 1"
              className="w-full h-full max-h-[300px] object-cover hover:opacity-95 transition-opacity"
            />
          </div>
          {images.slice(1).map((img, index) => (
            <div key={index + 1} className="cursor-pointer" onClick={() => openLightbox(index + 1)}>
              <img
                src={img}
                alt={`Post media ${index + 2}`}
                className="w-full h-full max-h-[150px] object-cover hover:opacity-95 transition-opacity"
              />
            </div>
          ))}
        </div>
        {renderLightbox()}
      </>
    );
  }

  // Four images - 2x2 grid
  if (images.length === 4) {
    return (
      <>
        <div className="grid grid-cols-2 gap-2 rounded-lg overflow-hidden">
          {images.map((img, index) => (
            <div key={index} className="cursor-pointer" onClick={() => openLightbox(index)}>
              <img
                src={img}
                alt={`Post media ${index + 1}`}
                className="w-full h-full max-h-[200px] object-cover hover:opacity-95 transition-opacity"
              />
            </div>
          ))}
        </div>
        {renderLightbox()}
      </>
    );
  }

  function renderLightbox() {
    return (
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="bg-black/95 border-none max-w-5xl p-0">
          <div className="relative">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/75 text-white"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Image */}
            <div className="flex items-center justify-center min-h-[400px] max-h-[80vh]">
              <img
                src={images[currentIndex]}
                alt={`Image ${currentIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain"
              />
            </div>

            {/* Navigation */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white"
                  onClick={prevImage}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white"
                  onClick={nextImage}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>

                {/* Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full text-white text-sm">
                  {currentIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return renderLightbox();
};

export default MediaGrid;
